import React, { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { updateOrganization, userLogin, userLogout } from '../api';
import useAsync from '../hooks/useAsync';
import { getOrganization } from '../api/admin';
import history from '../history';

const AppContext = createContext(undefined);

export const useApp = () => {
	const context = useContext(AppContext);
	if (!context) {
		throw new Error('useApp must be used within a AppProvider');
	}
	return context;
};

export const AppProvider = ({ children }) => {
	const navigate = useNavigate();

	const [theme, setTheme] = useState('light');
	const [isMobile, setIsMobile] = useState(false);
	const [isTablet, setIsTablet] = useState(false);
	const [isInitialized, setInitialized] = useState(false);

	useEffect(() => {
		const savedTheme = localStorage.getItem('theme');
		const initialTheme = savedTheme || 'light'; // Default to light theme
		setTheme(initialTheme);
		setInitialized(true);

		const handleResize = () => {
			const mobile = window.innerWidth < 768;
			const tablet = window.innerWidth < 1024;
			setIsMobile(mobile);
			setIsTablet(tablet);
		};

		handleResize();
		window.addEventListener('resize', handleResize);

		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, []);

	useEffect(() => {
		if (isInitialized) {
			localStorage.setItem('theme', theme);
			if (theme === 'dark') {
				document.documentElement.classList.add('dark');
			} else {
				document.documentElement.classList.remove('dark');
			}
		}
	}, [theme, isInitialized]);

	const {
		value: user,
		execute: onUserLogin,
		status: isLoggingIn,
		error,
	} = useAsync(userLogin, {
		immediate: false,
	});

	const { execute: onUpdateOrganization, status: isUpdatingOrg } = useAsync(
		updateOrganization,
		{ immediate: false },
	);

	const { execute: refreshOrganization } = useAsync(getOrganization, {
		immediate: false,
	});

	const localUser = JSON.parse(localStorage.getItem('user'));

	const getUser = (u) =>
		u
			? {
					email: u?.email,
					firstName: u?.firstName,
					lastName: u?.lastName,
					id: u?.id,
				}
			: null;

	return (
		<AppContext.Provider
			value={{
				theme,
				organization: user ? user?.organization : localUser?.organization,
				user: getUser(localUser || user),
				role: user ? user.role : localUser?.role,
				error,
				isLoggingIn: isLoggingIn === 'pending',
				onUpdateUser: (updated) => {
					const u = localStorage.getItem('user');
					const updatedUser = JSON.stringify({
						...JSON.parse(u),
						...updated,
					});

					localStorage.setItem('user', updatedUser);
					return JSON.parse(updatedUser);
				},
				login: (payload, cb) => {
					onUserLogin(payload, (response) => {
						localStorage.setItem('user', JSON.stringify(response));

						if (typeof cb === 'function') {
							cb();
						} else {
							navigate('/');
						}
					});
				},
				logout: () => {
					userLogout();
					navigate('/');
				},
				switchOrganization: (organizationId) => {
					onUpdateOrganization({ id: organizationId }, (payload) => {
						localStorage.setItem(
							'user',
							JSON.stringify({
								...localUser,
								organization: payload?.organization,
							}),
						);
						navigate('/');
					});
				},
				refreshOrganization: () => {
					refreshOrganization(localUser?.organization?.id, (payload) => {
						localStorage.setItem(
							'user',
							JSON.stringify({
								...localUser,
								organization: payload,
							}),
						);
						navigate('/');
					});
				},
				isMobile,
				isTablet: isTablet && !isMobile,
				isDesktop: !isMobile && !isTablet,
			}}
		>
			{children}
		</AppContext.Provider>
	);
};
